/**
 * BreakdownTab.jsx — Frontend-only analytical rollup of Total Holdings.
 *
 * Aggregates `processedData.total_holdings` by a user-selectable field
 * and shows the breakdown with weights, counts, and inline bars.
 *
 * Phase 35 — adds a "Sector → Industry" hierarchical mode where each
 * Industry row is grouped under its parent Sector, sorted by sector
 * weight (descending) then industry weight (descending) within sector.
 * Sectors render as sticky headers; industries render indented below.
 */
import { formatPercent, formatNumber } from "@/utils/format";
import { useMemo, useState, useEffect } from "react";
import { PieChart, ChevronRight, ChevronDown, Maximize2, Minimize2 } from "lucide-react";
import { FilterSelect } from "@/components/shared/FilterSelect";

const GROUP_FIELDS = [
  { key: "sector",            label: "Settore" },
  { key: "sector_industry",   label: "Settore → Industria" },
  { key: "asset_type",        label: "Asset class" },
  { key: "area",              label: "Area" },
  { key: "country",           label: "Paese" },
  { key: "currency",          label: "Valuta" },
  { key: "currency_area",     label: "Area valuta" },
];

const UNKNOWN_LABEL = "— Non noto";

const ASSET_TYPE_DISPLAY = {
  "Money Market": "Cash",
};

const displayLabel = (groupBy, raw) => {
  if (raw == null || raw === "") return UNKNOWN_LABEL;
  const value = String(raw);
  if (groupBy === "asset_type") return ASSET_TYPE_DISPLAY[value] || value;
  return value;
};

/* A9 — categorical palette from the shared chart tokens (light/dark parity). */
const BAR_COLORS = [
  "bg-chart-1", "bg-chart-2", "bg-chart-3", "bg-chart-4", "bg-chart-5",
  "bg-chart-6", "bg-chart-7", "bg-chart-8", "bg-chart-2", "bg-chart-4",
];

const formatPct = (v) => formatPercent(v || 0);


/**
 * Build the flat-list aggregation for simple modes.
 */
function aggregateFlat(data, groupBy) {
  const buckets = new Map();
  let totPct = 0;
  for (const row of data) {
    const raw = row[groupBy];
    const key = displayLabel(groupBy, raw);
    const bucket = buckets.get(key) || { group: key, count: 0, pct_total: 0 };
    bucket.count += 1;
    bucket.pct_total += Number(row.pct_total) || 0;
    buckets.set(key, bucket);
    totPct += Number(row.pct_total) || 0;
  }
  const rows = Array.from(buckets.values())
    .sort((a, b) => b.pct_total - a.pct_total);
  return { rows, totalPct: totPct, totalCount: data.length };
}


/**
 * Build the hierarchical Sector → Industry aggregation.
 *
 * Returns the same `rows / totalPct / totalCount` shape but each row
 * carries `level` (0 = sector header, 1 = industry under it) so the
 * renderer can indent / style accordingly.
 */
function aggregateSectorIndustry(data) {
  // First pass: collect (sector, industry) buckets.
  const byPair = new Map();    // "sector||industry" -> bucket
  const bySector = new Map();  // sector -> totals
  let totPct = 0;
  let totalCount = 0;
  for (const row of data) {
    const sector   = displayLabel("sector",   row.sector);
    const industry = displayLabel("industry", row.industry);
    const pct = Number(row.pct_total) || 0;
    const pairKey = `${sector}||${industry}`;
    const pair = byPair.get(pairKey)
      || { sector, industry, count: 0, pct_total: 0 };
    pair.count += 1;
    pair.pct_total += pct;
    byPair.set(pairKey, pair);

    const sec = bySector.get(sector)
      || { sector, count: 0, pct_total: 0, industries: 0 };
    sec.count += 1;
    sec.pct_total += pct;
    bySector.set(sector, sec);

    totPct += pct;
    totalCount += 1;
  }

  // Count distinct industries per sector for the header label.
  for (const pair of byPair.values()) {
    const sec = bySector.get(pair.sector);
    if (sec) sec.industries += 1;
  }

  // Sort sectors by total weight desc, then within each sector sort
  // industries by weight desc.
  const sortedSectors = Array.from(bySector.values())
    .sort((a, b) => b.pct_total - a.pct_total);

  const rows = [];
  for (const sec of sortedSectors) {
    rows.push({
      level: 0,
      key: `sector:${sec.sector}`,
      group: sec.sector,
      count: sec.count,
      pct_total: sec.pct_total,
      industries: sec.industries,
    });
    const industries = Array.from(byPair.values())
      .filter((p) => p.sector === sec.sector)
      .sort((a, b) => b.pct_total - a.pct_total);
    for (const ind of industries) {
      rows.push({
        level: 1,
        key: `industry:${sec.sector}||${ind.industry}`,
        group: ind.industry,
        parent_sector: sec.sector,
        count: ind.count,
        pct_total: ind.pct_total,
      });
    }
  }
  return { rows, totalPct: totPct, totalCount };
}


export function BreakdownTab({ data }) {
  const [groupBy, setGroupBy] = useState("sector");
  const [collapsedSectors, setCollapsedSectors] = useState(() => new Set());

  const { rows, totalPct, totalCount } = useMemo(() => {
    if (!data || data.length === 0) {
      return { rows: [], totalPct: 0, totalCount: 0 };
    }
    if (groupBy === "sector_industry") {
      return aggregateSectorIndustry(data);
    }
    return aggregateFlat(data, groupBy);
  }, [data, groupBy]);

  // Iter 92 Phase 5 — default-collapsed behaviour for the Sector → Industry
  // hierarchical mode. Every time the rows change (group switches into
  // sector_industry, or data refreshes) we re-seed the collapsed-set to
  // contain ALL the top-level sectors so the user lands on a clean
  // collapsed tree. They can then click open the ones they care about.
  useEffect(() => {
    if (groupBy !== "sector_industry") return;
    const sectorNames = rows
      .filter((r) => r.level === 0)
      .map((r) => r.group);
    setCollapsedSectors(new Set(sectorNames));
  }, [groupBy, rows]);

  // For the flat modes, max is the top bucket; for the hierarchical
  // mode, max is the largest sector (level-0 row).
  const maxPct = useMemo(() => {
    if (rows.length === 0) return 1;
    if (groupBy === "sector_industry") {
      const sectors = rows.filter((r) => r.level === 0);
      return sectors.length ? sectors[0].pct_total : 1;
    }
    return rows[0].pct_total;
  }, [rows, groupBy]);

  const toggleSector = (sectorName) => {
    setCollapsedSectors((prev) => {
      const next = new Set(prev);
      if (next.has(sectorName)) next.delete(sectorName);
      else next.add(sectorName);
      return next;
    });
  };

  const visibleRows = useMemo(() => {
    if (groupBy !== "sector_industry") return rows;
    return rows.filter(
      (r) => r.level === 0
        || !collapsedSectors.has(r.parent_sector),
    );
  }, [rows, groupBy, collapsedSectors]);

  const currentLabel = GROUP_FIELDS.find((f) => f.key === groupBy)?.label;

  return (
    <div data-testid="breakdown-tab">
      {/* Follow-up: stable geometry — context on the left, "Raggruppa per" +
          select anchored on the right. Conditional actions live on their own
          row so the select never moves when the grouping changes. */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-2 text-content-secondary text-xs min-w-0">
          <PieChart className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          Raggruppa le holdings e somma le % per ottenere l'esposizione per categoria.
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label htmlFor="breakdown-groupby" className="text-label uppercase text-content-muted tracking-wide">
            Raggruppa per
          </label>
          <FilterSelect
            id="breakdown-groupby"
            testid="breakdown-groupby"
            size="field"
            width="max-w-[220px]"
            sort={false}
            placeholder={null}
            value={groupBy}
            onChange={setGroupBy}
            options={GROUP_FIELDS.map((f) => ({ value: f.key, label: f.label }))}
            aria-label="Raggruppa le holdings per"
          />
        </div>
      </div>

      {groupBy === "sector_industry" && (
        <div
          data-testid="breakdown-tree-actions"
          className="flex flex-wrap items-center gap-2 mb-3"
        >
          <button
            data-testid="breakdown-expand-all"
            type="button"
            onClick={() => setCollapsedSectors(new Set())}
            className="inline-flex items-center gap-1 min-h-10 px-3 text-label uppercase tracking-wide
                       bg-surface border border-line rounded-md text-content-secondary
                       hover:bg-surface-subtle hover:text-content
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            aria-label="Espandi tutti i settori"
          >
            <Maximize2 className="w-3.5 h-3.5" aria-hidden="true" />
            Espandi tutto
          </button>
          <button
            data-testid="breakdown-collapse-all"
            type="button"
            onClick={() => {
              const all = rows
                .filter((r) => r.level === 0)
                .map((r) => r.group);
              setCollapsedSectors(new Set(all));
            }}
            className="inline-flex items-center gap-1 min-h-10 px-3 text-label uppercase tracking-wide
                       bg-surface border border-line rounded-md text-content-secondary
                       hover:bg-surface-subtle hover:text-content
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            aria-label="Comprimi tutti i settori"
          >
            <Minimize2 className="w-3.5 h-3.5" aria-hidden="true" />
            Comprimi tutto
          </button>
        </div>
      )}

      <div className="scrollable-table-container" style={{ height: "min(58vh, 620px)" }}>
        <div className="scrollable-table-scroll-area">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ minWidth: 220 }} scope="col">{currentLabel}</th>
                <th style={{ minWidth: 90, textAlign: "right" }} scope="col">Holdings</th>
                <th style={{ minWidth: 110, textAlign: "right" }} className="bg-table-selected" scope="col">
                  % totale
                </th>
                {/* Phase 6 — the bar is decoration: below 640px the numbers win. */}
                <th style={{ minWidth: 200 }} scope="col" className="hidden sm:table-cell">
                  Distribuzione
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r, idx) => {
                const isUnknown = r.group === UNKNOWN_LABEL;
                const widthPct = maxPct > 0 ? (r.pct_total / maxPct) * 100 : 0;
                if (groupBy === "sector_industry" && r.level === 0) {
                  const collapsed = collapsedSectors.has(r.group);
                  return (
                    <tr
                      key={r.key}
                      data-testid={`breakdown-sector-${r.group}`}
                      className="bg-surface/60 hover:bg-surface-subtle/60"
                    >
                      <td className={`font-bold ${isUnknown ? "text-content-muted italic" : "text-content"}`}>
                        {/* A5 — expand/collapse is a real control: 40px tall and keyboard-operable. */}
                        <button
                          type="button"
                          data-testid={`breakdown-sector-toggle-${r.group}`}
                          onClick={() => toggleSector(r.group)}
                          aria-expanded={!collapsed}
                          className="inline-flex items-center gap-1.5 min-h-10 -my-1 text-left rounded-md px-1 -mx-1
                                     hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                        >
                          {collapsed
                            ? <ChevronRight className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                            : <ChevronDown  className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
                          <span className="break-words">{r.group}</span>
                          <span className="text-content-muted font-normal text-label ml-1">
                            · {r.industries} {r.industries === 1 ? "industria" : "industrie"}
                          </span>
                        </button>
                      </td>
                      <td className="text-right text-content-secondary font-mono font-semibold">
                        {formatNumber(r.count)}
                      </td>
                      <td className="text-right font-bold text-content tabular bg-table-selected">
                        {formatPct(r.pct_total)}
                      </td>
                      <td className="hidden sm:table-cell">
                        <div className="w-full h-2 bg-surface-subtle rounded overflow-hidden">
                          <div
                            className="h-full bg-chart-1 transition-[width] duration-300"
                            style={{ width: `${widthPct}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                }
                // Flat row OR industry under a sector.
                const indentClass = (groupBy === "sector_industry" && r.level === 1)
                  ? "pl-8" : "";
                const barColor = isUnknown
                  ? "bg-surface-sunken"
                  : BAR_COLORS[idx % BAR_COLORS.length];
                return (
                  <tr
                    key={r.key || r.group}
                    data-testid={`breakdown-row-${r.group}`}
                  >
                    <td
                      className={`${indentClass} ${
                        isUnknown
                          ? "text-content-muted italic"
                          : (r.level === 1 ? "text-content-secondary" : "font-medium text-content")
                      }`}
                    >
                      <span className="block break-words">{r.group}</span>
                    </td>
                    <td className="text-right text-content-secondary font-mono">{formatNumber(r.count)}</td>
                    <td
                      className={`text-right tabular bg-table-selected/60 ${r.level === 1 ? "text-content-secondary" : "font-semibold text-content"}`}
                    >
                      {formatPct(r.pct_total)}
                    </td>
                    <td className="hidden sm:table-cell">
                      <div className="w-full h-2 bg-surface-subtle rounded overflow-hidden">
                        <div
                          className={`h-full ${barColor} transition-[width] duration-300`}
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-content-muted py-8">
                    Nessun dato disponibile.
                  </td>
                </tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-line">
                  <td className="text-content-secondary font-semibold">
                    {groupBy === "sector_industry"
                      ? `Totale · ${rows.filter((r) => r.level === 0).length} settori`
                      : `Totale · ${rows.length} gruppi`}
                  </td>
                  <td className="text-right text-content-secondary font-mono font-semibold">
                    {formatNumber(totalCount)}
                  </td>
                  <td className="text-right font-bold text-content tabular bg-table-selected">
                    {formatPct(totalPct)}
                  </td>
                  <td className="hidden sm:table-cell" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
